import React from 'react';
import type { ChartProps } from 'react-financial-charts';
import { Chart, ClickCallback } from 'react-financial-charts';
import { MouseCoordinateY } from '../Shapes/InteractiveShapes/MouseCoordinateY';
import ShapeManager from '../Shapes/ShapeManager';
import type { ChartId, ShapeSelection, MoreProps } from '../../types/commontypes';
import { useChartShapes, useChartUIContext } from '../../contexts';
import { getGlobalShapeState } from '../../contexts/GlobalShapesContext';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import { handleSquareClick, pricesDisplayFormat } from '../../utils/chartHandlers';
import type { StockData } from '../../types/chart';
import { OhlcvIndex } from '../../types/chart';

interface ChartSectionProps<TItem = unknown> {
  id: ChartId;
  height: number;
  origin?: ChartProps['origin'];
  yExtents?: ChartProps['yExtents'];
  yPanEnabled?: ChartProps['yPanEnabled'];
  padding?: ChartProps['padding'];
  children?: React.ReactNode;

  // Click handler
  onClick?: (
    e: React.MouseEvent<Element, MouseEvent>,
    moreProps: MoreProps<TItem>,
    shapeSelected: ShapeSelection | null,
  ) => void;
  onMouseDown?: (e: React.MouseEvent<Element, MouseEvent>, moreProps: MoreProps) => void;
  onPanEnd?: (e: React.MouseEvent<Element, MouseEvent>, moreProps: MoreProps) => void;
  // Optional: disable shape features for specific charts
  disableShapes?: boolean;
  disableVerticalLines?: boolean;

  // MouseCoordinateY props
  rectWidth?: number;

  // yAccessor for extracting close price from data item
  yAccessor?: (d: TItem) => number | undefined;
}

export function ChartSection<TItem = unknown>({
  id,
  height,
  origin,
  yExtents,
  yPanEnabled = false,
  padding,
  children,
  onClick,
  onMouseDown,
  onPanEnd,
  disableShapes = false,
  rectWidth = 80,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  yAccessor = (d: unknown) => (d as StockData).ohlcv[OhlcvIndex.CLOSE],
}: ChartSectionProps<TItem>): React.JSX.Element | null {
  // Get state from contexts
  const { chartUIStore, chartContainerRef } = useChartUIContext();
  const setYCoordinateValue = useChartUIStoreSelector(chartUIStore, s => s.setYCoordinateValue);
  const setYAccessorValue = useChartUIStoreSelector(chartUIStore, s => s.setYAccessorValue);
  const setActivePopup = useChartUIStoreSelector(chartUIStore, s => s.setActivePopup);
  const { drawState } = useChartShapes();

  // mousedown에서 처리했는지 추적하는 플래그.
  // RFC가 drag 모드로 가로채면 handleMouseDown이 호출되지 않으므로,
  // 이 플래그가 false인 채로 handleClick까지 도달하여 fallback 해제를 수행한다.
  const mouseDownHandledRef = React.useRef(false);

  // 도형 선택/해제를 mousedown에서 처리한다.
  // RFC 구독 순서: ShapeManager(도형) → ClickCallback이므로,
  // 도형의 onMouseDown이 먼저 실행되어 shapeJustSelected=true를 설정한 뒤 이 핸들러가 실행된다.
  const handleMouseDown = (e: React.MouseEvent<Element, MouseEvent>, moreProps: MoreProps<TItem>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    onMouseDown?.(e, moreProps as unknown as MoreProps);

    mouseDownHandledRef.current = true;

    const { shapeJustSelected, setShapeJustSelected, selectedShape } = getGlobalShapeState();

    if (shapeJustSelected) {
      setShapeJustSelected(false);
      onClick?.(e, moreProps, selectedShape);
    } else if (drawState?.status !== 'started') {
      onClick?.(e, moreProps, null);
    }
  };

  // Fallback: RFC가 drag 모드로 가로채면 handleMouseDown이 호출되지 않는다.
  // (selected + hovering인 VertLine이 다른 차트에도 렌더되어 draggable=true를 반환하는 경우)
  // 이때 click 이벤트에서 해제를 처리한다.
  const handleClick = (e: React.MouseEvent<Element, MouseEvent>, moreProps: MoreProps<TItem>) => {
    if (mouseDownHandledRef.current) {
      mouseDownHandledRef.current = false;
      return; // mousedown에서 이미 처리됨
    }

    const { shapeJustSelected, setShapeJustSelected, selectedShape } = getGlobalShapeState();

    if (shapeJustSelected) {
      setShapeJustSelected(false);
      onClick?.(e, moreProps, selectedShape);
    } else if (drawState?.status !== 'started') {
      onClick?.(e, moreProps, null);
    }
  };

  return (
    <Chart id={id} height={height} origin={origin} yExtents={yExtents} yPanEnabled={yPanEnabled} padding={padding}>
      {/* Add chart content passed as children */}
      {children}
      {/* MouseCoordinateY with (+) button */}
      <MouseCoordinateY
        rectWidth={rectWidth}
        displayFormat={pricesDisplayFormat}
        onSquareClick={(value, e, moreProps) =>
          handleSquareClick(
            value,
            e,
            moreProps,
            chartContainerRef,
            setYCoordinateValue,
            setYAccessorValue,
            setActivePopup,
            id,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            yAccessor as (d: unknown) => number | undefined,
          )
        }
        showSquareButton={true}
      />

      {/* Shape Manager */}
      {!disableShapes && <ShapeManager chartId={id} />}

      {/*If shape is not created, no need to track click*/}
      {!disableShapes && <ClickCallback onMouseDown={handleMouseDown} onClick={handleClick} onPanEnd={onPanEnd} />}
    </Chart>
  );
}
